/**
 * OnboardingV7 — production wrapper for the 15-step V7 onboarding flow.
 *
 * Spec: specs/v7-onboarding-production-migration.md
 * Mirrors the structure of `OnboardingPreview.jsx` (admin QA surface) but
 * swaps every local-only handler for a REAL DB write, real Stripe checkout
 * link, real push permission request, and real /chat handoff.
 *
 * Step persistence model:
 *   - Captures held in `localCaptures` while the user is on the step (so
 *     keystrokes don't fire DB writes)
 *   - Each step's `onContinue` calls `updateProfile(...)` with that step's
 *     captures BEFORE advancing. Partial completion is recoverable on return
 *     (captures persist; resume-on-mount lands them back at the first
 *     incomplete step — V1 just starts at step 1 always).
 *   - Step 15 (handoff) `onContinue` calls `completeOnboarding()` and then
 *     navigates to `/chat` (blank slate — the demo thread is saved as
 *     `firstConversationId` and shows up in the Recents sidebar instead
 *     of auto-loading. Users meet a fresh composer, not a pre-filled
 *     transcript; the AI demo conversation is one click away if they want it).
 *
 * Auth gates mirror the legacy `Onboarding.jsx`:
 *   - Not logged in → /login
 *   - Already completed → /account#preferences (unless ?resume=true URL
 *     param, used by PaymentSuccess to bounce the user back to step 15
 *     after Stripe round-trip)
 *
 * Steps 9 (founder-note) and 10 (trust) are gated `conditional: () => false`
 * in `onboardingSteps.js` so `getNextStep` skips them in production. Admin
 * preview passes `{ includeHidden: true }` to see them.
 */

import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Navigate } from 'react-router-dom'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { TOTAL_STEPS, getStepByNumber, getNextStep } from '@/data/onboardingSteps'
import { STEP_COMPONENTS } from '@/data/onboardingStepComponents'
import OnboardingProgressLogo from '@/components/onboarding/OnboardingProgressLogo'
import { capture } from '@/lib/posthog'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { createPerson } from '@/lib/queries/ministry'
import { getFoundingMemberCount } from '@/lib/queries/admin/founding-member'
import { STRIPE_LINKS } from '@/data/pricingData'

// localStorage key Ministry Map seed step writes so the user doesn't
// re-see the privacy disclaimer when they later open /ministry-map.
// Preserved exactly from the admin preview implementation.
const MINISTRY_MAP_DISCLAIMER_ACK_KEY = 'ministry_map_disclaimer_ack_v1'

function todayUtcDate() {
  // YYYY-MM-DD in UTC. Matches Postgres `date` column expectation.
  return new Date().toISOString().slice(0, 10)
}

function buildStripeUrl(planKey, user) {
  const base = STRIPE_LINKS[planKey]
  if (!base) return null
  const params = new URLSearchParams({
    client_reference_id: user.id,
    prefilled_email: user.email || '',
  })
  return `${base}?${params.toString()}`
}

export default function OnboardingV7() {
  const [searchParams] = useSearchParams()
  const { user, loading: sessionLoading } = useSession()
  const profileCtx = useProfile()
  const profile = profileCtx.profile
  const updateProfile = profileCtx.updateProfile
  const completeOnboarding = profileCtx.completeOnboarding

  const pushNotifications = usePushNotifications()

  // ── Step state ─────────────────────────────────────────────────
  const stepParam = parseInt(searchParams.get('step') || '1', 10)
  const initialStep = stepParam >= 1 && stepParam <= TOTAL_STEPS ? stepParam : 1
  const [step, setStep] = useState(initialStep)

  // In-memory captures for fields the user is mid-editing on a step
  // (e.g., typing into profile data fields). Persisted to DB on Continue.
  // After a step's Continue persists, the profile context's `profile`
  // becomes the source of truth and localCaptures for that step can be
  // emptied or left alone (idempotent).
  const [localCaptures, setLocalCaptures] = useState({})

  // Conversation id captured by the AI demo step (step 6). Held only in
  // memory — the conversation itself persists server-side via /chat edge fn.
  // No longer used for handoff navigation (we land users on the blank /chat
  // composer); kept around in case we want to surface the demo thread more
  // prominently in the Recents sidebar later.
  const [firstConversationId, setFirstConversationId] = useState(null)

  // Founding spots count for the paywall card (step 14). Fetched on mount
  // when step === 14 to avoid an unnecessary RPC for users who never hit
  // the paywall. Null = unknown, number = real count.
  const [foundingSpotsLeft, setFoundingSpotsLeft] = useState(null)

  // ── Auth + completion gates ────────────────────────────────────
  const authSettled = !sessionLoading && (!user || profile)

  // PaymentSuccess passes ?resume=true to bypass the "already completed"
  // guard so users returning from Stripe land at step 15 not /account.
  const resumeOverride = searchParams.get('resume') === 'true'
  const shouldRedirectCompleted = authSettled && !!profile?.onboardingCompleted && !resumeOverride

  // PostHog: step_viewed on every step change.
  useEffect(() => {
    if (!authSettled || shouldRedirectCompleted || !user) return
    const stepConfig = getStepByNumber(step)
    if (!stepConfig) return
    capture('onboarding_step_viewed', {
      step,
      label: stepConfig.title,
      step_id: stepConfig.id,
    })
  }, [step, authSettled, shouldRedirectCompleted, user])

  // Step 7 streak intro — fire-once write when user lands on this step.
  // Sets streak_count = 1, last_active_date = today, longest_streak =
  // max(existing, 1) so a re-runner doesn't lose their longest streak.
  const streakWriteFiredRef = useRef(false)
  useEffect(() => {
    if (step !== 7 || streakWriteFiredRef.current || !user) return
    streakWriteFiredRef.current = true
    const existingLongest = profile?.longest_streak || 0
    updateProfile({
      streakCount: 1,
      lastActiveDate: todayUtcDate(),
      longestStreak: Math.max(existingLongest, 1),
    }).catch(err => console.warn('Streak write failed (non-blocking):', err))
  }, [step, user, profile, updateProfile])

  // Step 14 paywall — fetch founding spots count on mount of this step.
  useEffect(() => {
    if (step !== 14) return
    let cancelled = false
    getFoundingMemberCount()
      .then(count => {
        if (!cancelled) {
          // getFoundingMemberCount returns USED count; we want remaining.
          // FOUNDING_SPOTS_TOTAL = 200 per pricingData.js convention.
          const remaining = Math.max(0, 200 - (count || 0))
          setFoundingSpotsLeft(remaining)
        }
      })
      .catch(err => {
        console.warn('Founding count fetch failed:', err)
        if (!cancelled) setFoundingSpotsLeft(0) // hide founding card on error
      })
    return () => {
      cancelled = true
    }
  }, [step])

  if (!authSettled) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-6 w-6 border-2 border-[var(--gold)] border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />
  if (shouldRedirectCompleted) return <Navigate to="/account#preferences" replace />

  const StepComponent = STEP_COMPONENTS[step]

  // Conditional-aware "next" — skips founder-note (9) + trust (10) which
  // are `conditional: () => false` on prod. Falls back to step+1 if no
  // conditional logic produces a target.
  const goNext = () => {
    const target = getNextStep(step, profile)
    setStep(target ? target.number : Math.min(step + 1, TOTAL_STEPS))
  }

  // ── Per-step prop assembly ─────────────────────────────────────
  // Mirrors OnboardingPreview's propsByStep but swaps mock handlers for
  // REAL DB writes / Stripe redirects / push permission flows.
  const propsByStep = {
    1: {
      displayName: profile?.first_name || profile?.display_name || 'friend',
      onContinue: goNext,
    },
    2: {
      // Read localCaptures FIRST so the selection visual updates immediately
      // on click (PrimaryGoalSynthesis gates Continue on `value` being set).
      // Without this merge the user clicks "Grow closer to God" → onSelect
      // fires + DB write starts async, but `value` stays null until the
      // ~500ms updateProfile round-trip resolves → Continue stays disabled
      // → user appears stuck on step 2. Caught by E2E walkthrough 2026-05-17.
      value: localCaptures.primary_goal ?? profile?.primary_goal,
      onSelect: val => {
        setLocalCaptures(c => ({ ...c, primary_goal: val }))
        // Persist immediately on select — single-choice picker, no
        // round-trip state. User can change mind by re-clicking before
        // Continue and the second updateProfile call overwrites cleanly.
        updateProfile({ primaryGoal: val }).catch(err =>
          console.warn('primary_goal write failed:', err)
        )
      },
      onContinue: goNext,
    },
    3: {
      // Same localCaptures-first pattern — read from local state so the
      // selection visual updates immediately, even though step 3 doesn't
      // pre-fill from profile.
      primaryGoal: localCaptures.primary_goal ?? profile?.primary_goal,
      // Step 3's whole point IS the selection gesture — don't pre-populate
      // even if profile.current_struggles already has values.
      value: localCaptures.current_struggles ?? [],
      onToggle: val =>
        setLocalCaptures(c => {
          const cur = c.current_struggles ?? []
          const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val]
          return { ...c, current_struggles: next }
        }),
      onContinue: async () => {
        const struggles = localCaptures.current_struggles ?? []
        // Allow Continue even with 0 selected (HungerChips synthesis shows
        // "Skip for now" label in that case per existing component contract).
        if (struggles.length > 0) {
          await updateProfile({ currentStruggles: struggles })
        }
        goNext()
      },
    },
    4: {
      topStruggle:
        (localCaptures.current_struggles || profile?.current_struggles || [])[0] ||
        'staying_consistent',
      struggles: localCaptures.current_struggles || profile?.current_struggles || [],
      onContinue: goNext,
    },
    5: { onContinue: goNext }, // Doctrinal trust — no capture
    6: {
      profile: { ...profile, ...localCaptures },
      onContinue: goNext,
      onConversationCreated: id => setFirstConversationId(id),
    },
    7: {
      // Streak intro — actual streak write fires from the useEffect above,
      // not on Continue. Component just animates and advances.
      displayName: profile?.first_name || profile?.display_name || 'friend',
      onContinue: goNext,
    },
    8: {
      onAdd: async input => {
        try {
          await createPerson(user.id, input)
          try {
            localStorage.setItem(MINISTRY_MAP_DISCLAIMER_ACK_KEY, '1')
          } catch {
            /* private mode / quota — non-fatal */
          }
        } catch (err) {
          console.warn('Ministry Map createPerson failed:', err)
          // Non-blocking — let user advance even if DB write fails. They
          // can retry on /ministry-map proper.
        }
        goNext()
      },
      onSkip: goNext,
    },
    // 9 founder-note: hidden on prod (conditional: false). Admin preview only.
    // 10 trust: hidden on prod (conditional: false). Admin preview only.
    11: {
      role: localCaptures.role ?? profile?.role,
      denomination: localCaptures.denomination ?? profile?.denomination,
      preferredTranslation: localCaptures.preferredTranslation ?? profile?.preferred_translation,
      personalizeToDenomination:
        localCaptures.personalizeToDenomination ?? profile?.personalize_experience ?? true,
      interests: localCaptures.interests ?? profile?.interests ?? [],
      age: localCaptures.age ?? profile?.age,
      days: localCaptures.bible_reading_days_per_week ?? profile?.bible_reading_days_per_week,
      minutes: localCaptures.daily_time_goal ?? profile?.daily_time_goal,
      // displayName + firstName — passed through so ProfileData synthesis
      // can show a Name section when displayName is empty (Mason 2026-05-17:
      // trading card was showing "Welcome, friend." for accounts without a
      // captured name). Prefers in-flight localCaptures over context profile.
      displayName: localCaptures.displayName ?? profile?.displayName,
      firstName: profile?.first_name,
      onChange: patch => setLocalCaptures(c => ({ ...c, ...patch })),
      onContinue: async () => {
        // Batched write — every field the user touched in localCaptures
        // persists in one call. Skip writes for fields not in localCaptures
        // (avoids clobbering existing profile values with undefined).
        const payload = {}
        if (localCaptures.displayName !== undefined) payload.displayName = localCaptures.displayName
        if (localCaptures.role !== undefined) payload.role = localCaptures.role
        if (localCaptures.denomination !== undefined)
          payload.denomination = localCaptures.denomination
        if (localCaptures.preferredTranslation !== undefined)
          payload.preferredTranslation = localCaptures.preferredTranslation
        if (localCaptures.personalizeToDenomination !== undefined)
          payload.personalizeExperience = localCaptures.personalizeToDenomination
        if (localCaptures.interests !== undefined) payload.interests = localCaptures.interests
        if (localCaptures.age !== undefined) payload.age = localCaptures.age
        if (localCaptures.bible_reading_days_per_week !== undefined)
          payload.bibleReadingDaysPerWeek = localCaptures.bible_reading_days_per_week
        if (localCaptures.daily_time_goal !== undefined)
          payload.dailyTimeGoal = localCaptures.daily_time_goal
        if (Object.keys(payload).length > 0) {
          await updateProfile(payload)
        }
        goNext()
      },
    },
    12: {
      profile,
      ministryMapSeeded: false, // V1: don't query ministry_people to check; cosmetic recap only
      onContinue: goNext,
    },
    13: {
      reminderMethod: localCaptures.reminder_method ?? profile?.reminder_method ?? null,
      onMethodChange: val => setLocalCaptures(c => ({ ...c, reminder_method: val })),
      onContinue: async () => {
        const method = localCaptures.reminder_method ?? profile?.reminder_method
        // If user picked notifications, fire the real permission flow.
        // If denied, downgrade method to 'none' so we don't store a value
        // we can't fulfill.
        let effectiveMethod = method
        if (method === 'notifications') {
          try {
            const result = await pushNotifications.requestPermission()
            if (result === 'granted') {
              await pushNotifications.subscribe()
            } else {
              effectiveMethod = 'none' // user declined OS prompt
            }
          } catch (err) {
            console.warn('Push permission failed:', err)
            effectiveMethod = 'none'
          }
        }
        await updateProfile({ reminderMethod: effectiveMethod })
        goNext()
      },
    },
    14: {
      onStartTrial: () => {
        const url = buildStripeUrl('monthly', user)
        if (url) window.location.href = url
      },
      onChooseAnnual: () => {
        const url = buildStripeUrl('annual', user)
        if (url) window.location.href = url
      },
      onChooseFounding: () => {
        const url = buildStripeUrl('founding', user)
        if (url) window.location.href = url
      },
      onContinueFree: goNext,
      hasUsedTrial: !!profile?.has_used_trial,
      foundingSpotsLeft, // null until fetch resolves; component handles null
    },
    15: {
      displayName: profile?.first_name || profile?.display_name || 'friend',
      firstConversationId,
      // Plan tier from the real subscription column. Maps the DB tier strings
      // (premium_monthly / premium_annual / founding_annual) to the pill
      // variants the handoff expects (trial / annual / founding / free).
      planTier: (() => {
        if (profile?.is_trial) return 'trial'
        const plan = profile?.subscription_plan
        if (plan === 'founding_annual') return 'founding'
        if (plan === 'premium_annual') return 'annual'
        if (plan === 'premium_monthly') return profile?.is_trial ? 'trial' : 'annual' // monthly users see annual pill style
        return 'free'
      })(),
      onContinue: async () => {
        // CRITICAL: await completeOnboarding BEFORE navigate, otherwise
        // AppLayout's redirect guard sees onboarding_completed=false and
        // bounces user back to /onboarding (infinite loop risk).
        try {
          await completeOnboarding()
          capture('onboarding_completed')
        } catch (err) {
          console.error('completeOnboarding failed:', err)
          // Still navigate — don't trap user on handoff if MailerLite or
          // similar non-critical side effect failed. AppLayout redirect
          // will re-run them through if onboarding_completed truly didn't
          // flip; refresh + retry is cleaner than blocking here.
        }
        // Always land on the blank-slate /chat. The onboarding demo
        // thread already exists in their conversation history (saved as
        // firstConversationId) and will appear in the Recents sidebar —
        // they can pop back into it if they want, but the default first
        // experience is a fresh composer, not a pre-loaded transcript.
        window.location.href = '/chat'
      },
    },
  }

  const props = propsByStep[step] || { onContinue: goNext }

  // `w-full` is REQUIRED on the outer flex container below because AuthLayout's
  // <main> uses `flex items-start justify-center` with no width set on its
  // children — without w-full here, the div collapses to the natural width
  // of its narrowest descendant (~448px) and `max-w-5xl` on the inner div
  // has nothing to expand into. Caught 2026-05-17 — Mason's first widening
  // deploy looked unchanged on /onboarding even though admin preview was
  // fine (admin preview has explicit grid widths from AdminLayout).
  return (
    <div className="w-full min-h-screen flex flex-col items-center bg-[var(--bg-base)]">
      {/* Outer column max-width tiered up at lg+ so desktop users see content
          using a real desktop width (was max-w-3xl/768px, which left ~70% of
          a 1920px screen empty). Step synthesis components have their own
          max-w that also bump on lg+ — see each step's outer wrapper. */}
      <div className="w-full max-w-3xl lg:max-w-5xl xl:max-w-6xl px-4 py-6">
        <div className="flex justify-center mb-4">
          <OnboardingProgressLogo step={step} totalSteps={TOTAL_STEPS} />
        </div>
        {StepComponent ? (
          <StepComponent {...props} />
        ) : (
          // Founder note (9) is conditional-false — shouldn't render in prod,
          // but if a user manually navigates ?step=9 we render a soft fallback.
          <div className="text-center py-20 text-[var(--text-muted)]">
            <p className="text-sm">This step isn&rsquo;t available right now.</p>
            <button
              type="button"
              onClick={goNext}
              className="mt-4 text-[var(--gold)] underline text-sm"
            >
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
